import type {
  GenPrompt,
  GenPromptRecord,
  UpsertGenPromptRecord,
} from '@musedrops/shared';
import { fromDbKeys, toDbKeys } from '@musedrops/shared';
import { supabase } from '../services/supabase';

function mapToPromptTemplate(record: GenPromptRecord): GenPrompt {
  return {
    id: record.id,
    key: record.key,
    promptTemplate: record.prompt,
    modelPresetId: record.modelPreset,
  };
}

export async function getGenPromptRecord(
  key: string
): Promise<GenPromptRecord> {
  const { data, error } = await supabase
    .from('gen_prompts')
    .select('*')
    .eq('key', key)
    .single();

  if (error) {
    throw new Error(`Failed to load prompt ${key}: ${error.message}`);
  }

  if (!data) {
    throw new Error(`Prompt ${key} not found`);
  }

  const record = fromDbKeys<GenPromptRecord>(data as Record<string, unknown>);

  if (!record.prompt || !record.modelPreset) {
    throw new Error(`Prompt ${key} configuration missing or invalid`);
  }

  return record;
}

export async function fetchGenPrompt(key: string): Promise<GenPrompt> {
  const record = await getGenPromptRecord(key);
  return mapToPromptTemplate(record);
}

export async function upsertGenPrompt(
  record: UpsertGenPromptRecord
): Promise<GenPromptRecord> {
  const payload = toDbKeys(record);

  const { data, error } = await supabase
    .from('gen_prompts')
    .upsert(payload, { onConflict: 'key' })
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to upsert prompt ${record.key}: ${error.message}`);
  }

  return fromDbKeys<GenPromptRecord>(data as Record<string, unknown>);
}

export async function updateGenPromptModelPreset(
  key: string,
  modelPreset: string
): Promise<void> {
  const { error } = await supabase
    .from('gen_prompts')
    .update(toDbKeys({ modelPreset }))
    .eq('key', key);

  if (error) {
    throw new Error(`Failed to update prompt model preset: ${error.message}`);
  }
}

export async function getGenPromptContent(key: string): Promise<string | null> {
  try {
    const record = await getGenPromptRecord(key);
    return record.prompt;
  } catch (error) {
    if (error instanceof Error && error.message.includes('not found')) {
      return null;
    }
    throw error;
  }
}
